import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/base/Reveal';
import { accent } from '@/components/feature/solution/theme';

interface Layer {
  tag: string;
  name: string;
  icon: string;
  items: string[];
}

interface SystemItem {
  icon: string;
  title: string;
  type: string;
  desc: string;
}

interface ArchitectureData {
  eyebrow: string;
  title: string;
  subtitle: string;
  layers: Layer[];
  systemsTitle: string;
  systemsNote: string;
  systems: SystemItem[];
}

export default function LogisticsArchitecture({ data }: { data: ArchitectureData }) {
  const a = accent('green');

  return (
    <section id="architecture" className="w-full px-4 md:px-6 py-16 md:py-24 bg-background-100 scroll-mt-20">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex justify-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-100 text-accent-900 text-xs font-semibold tracking-wide whitespace-nowrap">
            <i className="ri-node-tree"></i>
            {data.eyebrow}
          </span>
        </div>
        <div className="mt-4">
          <SectionHeading title={data.title} subtitle={data.subtitle} />
        </div>

        {/* 软硬件系统拓扑图 */}
        <Reveal className="mt-8 md:mt-10">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-3 md:p-5">
            <div className="w-full aspect-[1100/1211] overflow-hidden rounded-xl">
              <img
                src="https://static.readdy.ai/image/4482546e014370507f3d90ff31782979/f85531c2a8533728a9b60adc290c156e.png"
                alt="智慧物流软硬件系统拓扑图，展示平台层、IoT 物联网连接平台、网络传输层与现场感知设备的上下行数据流向"
                title="智慧物流软硬件系统拓扑图"
                className="w-full h-full object-contain object-top"
              />
            </div>
          </div>
        </Reveal>

        {/* 核心软硬件系统清单 */}
        <Reveal className="mt-14 md:mt-16">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">{data.systemsTitle}</h3>
            <p className="text-xs md:text-sm text-foreground-600">{data.systemsNote}</p>
          </div>
        </Reveal>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.systems.map((sys, idx) => (
            <Reveal key={sys.title} delay={idx * 50}>
              <div className="flex h-full items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
                <span className={`w-11 h-11 shrink-0 flex items-center justify-center rounded-lg ${a.iconBox}`}>
                  <i className={`${sys.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h4 className="font-heading font-semibold text-sm md:text-base text-foreground-950">
                      {sys.title}
                    </h4>
                    <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[10px] font-semibold whitespace-nowrap">
                      {sys.type}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-foreground-700 leading-relaxed">{sys.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}